import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { clsx } from 'clsx';

type Variant = 'primary' | 'soft' | 'outline' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

const base =
  'inline-flex items-center justify-center gap-2 font-semibold select-none rounded-none press ' +
  'disabled:opacity-40 disabled:pointer-events-none transition-colors';

const variants: Record<Variant, string> = {
  primary:
    'accent-aurora text-white ' +
    'shadow-[inset_0_-1px_0_rgba(0,0,0,0.06)] ' +
    'hover:brightness-[1.04] active:brightness-95',
  soft: 'bg-accent-soft text-accent-strong hover:bg-accent-soft/80',
  outline: 'border border-hairline bg-surface text-ink hover:bg-surface-2/60',
  ghost: 'text-accent hover:text-accent-strong',
  danger:
    'bg-linear-to-b from-[#cf3b40] to-[var(--color-danger)] text-white ' +
    'shadow-[inset_0_1px_0_rgba(255,255,255,0.28),inset_0_-1px_0_rgba(0,0,0,0.06)] ' +
    'hover:brightness-[1.05] active:brightness-95',
};

const sizes: Record<Size, string> = {
  sm: 'h-9 px-4 text-[13px]',
  md: 'h-11 px-5 text-[15px]',
  lg: 'h-13 px-6 text-[16px]',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  block?: boolean;
  children?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', block, className, children, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={clsx(base, variants[variant], sizes[size], block && 'w-full', className)}
      {...rest}
    >
      {children}
    </button>
  );
});

export const IconButton = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { label: string; size?: 'sm' | 'md' }
>(function IconButton({ label, className, size = 'md', children, type = 'button', ...rest }, ref) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      className={clsx(
        'inline-flex items-center justify-center rounded-none press text-ink',
        size === 'sm' ? 'size-9' : 'size-10',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
});
